import type { ReactNode } from 'react';
import { Layout } from '../common/Layout';

/**
 * Width contracts for full-screen pages. The cap is applied to BOTH
 * the header content and the body so they always optically line up
 * on desktop — that misalignment was the single most-reported bug.
 *
 *   - `list`    →  768px (max-w-3xl)   chat lists, settings, my-agent, tasks
 *   - `feed`    → 1024px (max-w-5xl)   masonry feeds with multi-column cards
 *   - `reading` →  640px (max-w-2xl)   articles, forms, edit screens
 *   - `full`    →  no cap              explore, anything that owns its own grid
 */
export type PageWidth = 'list' | 'feed' | 'reading' | 'full';

const WIDTH_CLASS: Record<PageWidth, string> = {
  list:    'max-w-3xl',
  feed:    'max-w-5xl',
  reading: 'max-w-2xl',
  full:    '',
};

export interface PageContainerProps {
  /** Header slot — typically a `<PageHeader>` or `<DetailHeader>`. The
   * container takes care of wrapping it in the same max-width column
   * as the body so right-aligned controls don't drift to the viewport
   * edge on desktop. */
  header?: ReactNode;
  /** Show the bottom tab bar. Defaults to true for tab-root pages. */
  showTabBar?: boolean;
  /** Width contract — see PageWidth. Default `list` (768px) since
   * the vast majority of pages are list-shaped. Pages that own their
   * own grid (Explore, Feed masonry) should opt into `feed` or `full`. */
  width?: PageWidth;
  /** When true, skip the inner scroll wrapper and `pb-*` spacing so
   * children can own a full-height flex frame. Use for chat-style
   * surfaces where the composer must be pinned to the bottom of the
   * viewport and the message thread scrolls independently. */
  bareBody?: boolean;
  children: ReactNode;
}

/**
 * Top-level page wrapper. Use this on every full-screen route instead
 * of reaching for `<Layout>` directly — `<Layout>` is the lower-level
 * shell that owns the side nav and tab bar; `<PageContainer>` is the
 * higher-level contract that also encodes desktop max-width and
 * header/body alignment.
 *
 * @example
 * <PageContainer
 *   header={<PageHeader title="对话" actions={...} />}
 *   width="list"
 * >
 *   <List>...</List>
 * </PageContainer>
 */
export function PageContainer({
  header,
  showTabBar = true,
  width = 'list',
  bareBody = false,
  children,
}: PageContainerProps) {
  const widthClass = WIDTH_CLASS[width];

  return (
    <Layout
      header={
        header ? (
          <div className={`${widthClass} mx-auto w-full`}>
            {header}
          </div>
        ) : undefined
      }
      showTabBar={showTabBar}
      bareBody={bareBody}
    >
      <div className={`${widthClass} mx-auto w-full ${bareBody ? 'flex-1 min-h-0 flex flex-col' : ''}`}>
        {children}
      </div>
    </Layout>
  );
}
